import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16 } from "styled-components";
import React from "react";
import { hasItems, padNumber, takeFirst } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type MediaWithHighlightsItemView = {
  label?: string | null;
  text?: string | null;
};
export type MediaWithHighlightsBlockViewProps<TItem = MediaWithHighlightsItemView> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasCounters?: boolean | null;
  hasTopDivider?: boolean | null;
  highlights?: readonly TItem[] | null;
  media?: React.ReactNode;
  mode?: string | null;
  renderHighlight: (item: TItem, index: number, count?: string) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function MediaWithHighlightsBlockView<TItem = MediaWithHighlightsItemView>({
  anchor,
  getItemKey,
  hasCounters = false,
  hasTopDivider = true,
  highlights = [],
  media,
  mode = "light",
  renderHighlight,
  sectionProps,
  theme = "white"
}: MediaWithHighlightsBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const items = highlights ?? [];
  if (!hasItems(items)) {
    return null;
  }
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledGrid $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.border]}>
            <_StyledDiv2 $_css3={[{
            "gridColumn": "1 / -1",
            "borderBottomWidth": "1px",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 6 / span 6",
              "borderBottomWidth": "0px",
              "borderRightWidth": "1px"
            }
          }, themeStyle.border]}>
              <_StyledDiv3>{media}</_StyledDiv3>
            </_StyledDiv2>
            <_StyledDiv4 $_css4={[{
            "gridColumn": "1 / -1",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 6 / span 6"
            }
          }, themeStyle.background]}>
              {items.map((highlight, idx) => {
              const count = hasCounters ? padNumber(idx + 1) : undefined;
              return <_StyledDiv5 key={getItemKey?.(highlight, idx) ?? idx} $_css5={[{
                "borderBottomWidth": "1px",
                "paddingLeft": "4rem",
                "paddingRight": "4rem",
                "paddingTop": "2.4rem",
                "paddingBottom": "2.4rem",
                "&:last-child": {
                  "borderBottomWidth": "0px"
                },
                "@media (min-width: 1024px)": {
                  "paddingTop": "4rem",
                  "paddingBottom": "4rem"
                }
              }, themeStyle.border]}>
                    {renderHighlight(highlight, idx, count)}
                  </_StyledDiv5>;
            })}
            </_StyledDiv4>
          </_StyledGrid>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
export function MediaWithHighlightsBlockItem({
  count,
  label,
  text
}: {
  count?: string;
  label?: string | null;
  text?: string | null;
}) {
  return <>
      {count ? <_StyledDiv6>
          <_StyledDiv7>
            <_StyledDiv8>
              {count}
            </_StyledDiv8>
            {label && <_StyledH>{label}</_StyledH>}
          </_StyledDiv7>

          <_StyledSpan />
        </_StyledDiv6> : <_StyledDiv9>
          {label && <_StyledH2>{label}</_StyledH2>}
          <_StyledSpan2 />
        </_StyledDiv9>}
      {text && <_StyledP>{text}</_StyledP>}
    </>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-14ulpsq-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-14ulpsq-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-14ulpsq-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-14ulpsq-3"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-14ulpsq-4"
})({
  "display": "flex",
  "height": "100%",
  "alignItems": "center",
  "padding": "1.6rem",
  "@media (min-width: 1024px)": {
    "padding": "4rem"
  }
});
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-14ulpsq-5"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-14ulpsq-6"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled8("div").withConfig({
  componentId: "sc-sf-14ulpsq-7"
})({
  "display": "flex",
  "alignItems": "flex-start",
  "justifyContent": "space-between",
  "gap": "0.8rem",
  "@media (min-width: 1024px)": {
    "alignItems": "center",
    "gap": "2.4rem"
  }
});
var _StyledDiv7 = _styled9("div").withConfig({
  componentId: "sc-sf-14ulpsq-8"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "0.8rem",
  "@media (min-width: 1024px)": {
    "flexDirection": "row",
    "alignItems": "center",
    "gap": "2.4rem"
  }
});
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-14ulpsq-9"
})({
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "space-between",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledH = _styled11("h3").withConfig({
  componentId: "sc-sf-14ulpsq-10"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledSpan = _styled12("span").withConfig({
  componentId: "sc-sf-14ulpsq-11"
})({
  "marginLeft": "auto",
  "marginTop": "0.8rem",
  "height": "0.8rem",
  "width": "0.8rem",
  "flexShrink": "0",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
  "@media (min-width: 1024px)": {
    "marginTop": "0"
  }
});
var _StyledDiv9 = _styled13("div").withConfig({
  componentId: "sc-sf-14ulpsq-12"
})({
  "display": "flex",
  "gap": "1.6rem",
  "@media (min-width: 1024px)": {
    "gap": "2.4rem"
  }
});
var _StyledH2 = _styled14("h3").withConfig({
  componentId: "sc-sf-14ulpsq-13"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledSpan2 = _styled15("span").withConfig({
  componentId: "sc-sf-14ulpsq-14"
})({
  "marginLeft": "auto",
  "marginTop": "0.8rem",
  "height": "0.8rem",
  "width": "0.8rem",
  "flexShrink": "0",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
});
var _StyledP = /*#__PURE__*/_styled16("p").withConfig({
  displayName: "media-with-highlights___StyledP",
  componentId: "sc-17omtbs-0"
})({
  "marginTop": "0.8rem",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(23 23 23 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});